<template>
  <div class="base-autocomplete">
    <ComboboxRoot
      v-slot="{ open }"
      v-model="model"
      by="value"
      class="w-100%"
      ignore-filter
      open-on-click
      open-on-focus
      @update:open="handleOpenChange"
    >
      <ComboboxAnchor class="base-autocomplete__input-wrapper">
        <ComboboxInput
          v-model="query"
          :display-value="
            (option) => {
              return (option as Option<T> | undefined)?.text ?? ''
            }
          "
          class="base-autocomplete__input"
          :placeholder="placeholder"
        />

        <ComboboxTrigger aria-label="Pokaż opcje" class="base-autocomplete__icon">
          <BaseIcon :name="open ? 'expand_more' : 'expand_less'" />
        </ComboboxTrigger>
      </ComboboxAnchor>

      <ComboboxContent class="base-autocomplete__content">
        <ComboboxViewport
          class="base-autocomplete__options"
          style="max-height: 250px; overflow-y: auto"
        >
          <div v-if="filteredOptions.length === 0" class="base-autocomplete__empty">
            Brak wyników
          </div>
          <ComboboxVirtualizer
            v-else
            v-slot="{ option }"
            :estimate-size="props.dynamicHeight ? 48 : 26"
            :options="filteredOptions"
            :text-content="option => option.text"
          >
            <ComboboxItem
              class="base-autocomplete__option"
              :class="{
                'base-autocomplete__option--dynamic-height': props.dynamicHeight,
              }"
              :disabled="option.disabled"
              :text-value="option.text"
              :value="option"
            >
              <slot name="option" :option="option">
                <span class="base-autocomplete__option-text">{{ option.text }}</span>
              </slot>
            </ComboboxItem>
          </ComboboxVirtualizer>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxRoot>
  </div>
</template>

<script lang="ts" setup generic="T extends string | number">
  import {
    ComboboxAnchor,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxRoot,
    ComboboxTrigger,
    ComboboxViewport,
    ComboboxVirtualizer,
  } from 'reka-ui'
  import { computed, ref } from 'vue'

  import BaseIcon from '@/components/BaseIcon.vue'

  type Option<T extends string | number> = {
    disabled?: boolean
    text: string
    value: T
    [key: string]: unknown
  }

  const props = withDefaults(
    defineProps<{ dynamicHeight?: boolean; options: Option<T>[]; placeholder?: string }>(),
    { dynamicHeight: false },
  )
  const model = defineModel<Option<T>>()

  const query = ref('')
  const options = computed(() => props.options.slice().sort((a, b) => a.text.localeCompare(b.text)))
  const filteredOptions = computed(() =>
    query.value === ''
      ? options.value
      : options.value.filter(v =>
          v.text
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.value.toLowerCase().replace(/\s+/g, '')),
        ),
  )

  const handleOpenChange = (open: boolean) => {
    if (open && query.value === model.value?.text) query.value = ''
  }
</script>

<style scoped>
  .base-autocomplete {
    position: relative;
  }

  .base-autocomplete__input-wrapper {
    position: relative;
    width: 100%;
  }

  .base-autocomplete__input {
    width: 100%;
    padding: 5px 20px 5px 8px;
    font-size: 12px;
    line-height: 15px;
    outline: 0;
    border: 0;
    border-bottom: 1px solid var(--color-tertiary-text);
  }

  .base-autocomplete__input-wrapper:hover .base-autocomplete__input {
    border-bottom: 1px solid var(--color-primary-text);
  }

  .base-autocomplete__input-wrapper .base-autocomplete__input:focus {
    border-bottom: 1px solid var(--color-main-100);
  }

  .base-autocomplete__icon {
    position: absolute;
    top: 3px;
    right: 0;
    color: var(--color-tertiary-text);
    cursor: pointer;
    padding: 0;
    background: transparent;
    border: 0;
  }

  .base-autocomplete__input-wrapper:hover .base-autocomplete__icon {
    color: var(--color-primary-text);
  }

  .base-autocomplete__input-wrapper.active .base-autocomplete__icon {
    color: var(--color-main-100);
  }

  .base-autocomplete__content {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 100;
    min-width: 100%;
    max-width: 100%;
    background: #fff;
    box-shadow: 0 1px 6px 0 rgb(155 155 155 / 50%);
  }

  .base-autocomplete__options {
    max-height: 250px;
    overflow-y: auto;
    scrollbar-width: thin;
  }

  .base-autocomplete__options::-webkit-scrollbar {
    width: 6px;
  }

  .base-autocomplete__option {
    z-index: 101;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: stretch;
    width: 100%;
    height: 26px;
    padding: 0 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    line-height: 26px;
    overflow-wrap: break-word;
    white-space: nowrap;
    cursor: pointer;
  }

  .base-autocomplete__option--dynamic-height {
    height: auto;
    min-height: 26px;
    padding: 4px 10px;
    overflow: visible;
    text-overflow: unset;
    line-height: 1.4;
    white-space: normal;
  }

  .base-autocomplete__option-text {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: break-word;
    white-space: nowrap;
  }

  .base-autocomplete__option:hover,
  .base-autocomplete__option[data-highlighted],
  .base-autocomplete__option[data-state="checked"] {
    outline: none;
    background: var(--color-main-10);
  }

  .base-autocomplete__empty {
    height: 26px;
    padding: 0 10px;
    font-size: 12px;
    line-height: 26px;
  }
</style>
